import React, { useState } from 'react';
import { X, Copy, Loader2 } from 'lucide-react';

interface DuplicateQuoteModalProps {
  quotation: {
    name: string;
  } | null;
  isOpen: boolean;
  isDuplicating: boolean;
  onConfirm: (newName: string) => void;
  onCancel: () => void;
}

export function DuplicateQuoteModal({
  quotation,
  isOpen,
  isDuplicating,
  onConfirm,
  onCancel
}: DuplicateQuoteModalProps) {
  const [newName, setNewName] = useState(quotation ? `${quotation.name} (Copy)` : '');

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onConfirm(newName);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-violet-50 flex items-center justify-center">
              <Copy className="w-5 h-5 text-violet-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-semibold text-slate-900">Duplicate Quotation</h2>
              <p className="mt-1 text-[14px] text-slate-500">Create a copy with a new name.</p>
            </div>
            <button
              onClick={onCancel}
              className="flex-shrink-0 p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="px-6 pb-2">
            <label htmlFor="newName" className="block text-[13px] font-medium text-slate-700 mb-1.5">
              New Quotation Name
            </label>
            <input
              type="text"
              id="newName"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="w-full"
              placeholder="Enter a name for the duplicate"
              required
            />
          </div>

          <div className="px-6 py-4 mt-2 bg-slate-50 border-t border-slate-100 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={isDuplicating}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isDuplicating}
              className="btn-primary"
            >
              {isDuplicating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Duplicating...
                </>
              ) : (
                'Duplicate'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
